import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { TimetableData } from '@/types/timetable'
import { useTranslation } from 'react-i18next'

interface BasicTimetableInfoProps {
  timetableData: TimetableData
  handleInputChange: (field: string, value: any) => void
}

export function BasicTimetableInfo({
  timetableData,
  handleInputChange,
}: BasicTimetableInfoProps) {
  const { t } = useTranslation('timetable', { keyPrefix: 'timetable-folder'})

  return (
    <div className="space-y-6">
      {/* Timetable Name */}
      <div data-tour="timetable-name">
        <Label htmlFor="timetableName">{t('timetableForm.timetableName')}</Label>
        <Input
          id="timetableName"
          placeholder={t('timetableForm.timetableNamePlaceholder')}
          value={timetableData.name || ''}
          onChange={(e) => handleInputChange('name', e.target.value)}
        />
      </div>

      {/* Academic Year */}
      <div data-tour="academic-year">
        <Label htmlFor="academicYear">{t('timetableForm.academicYear')}</Label>
        <Input
          id="academicYear"
          placeholder={t('timetableForm.academicYearPlaceholder')}
          value={timetableData.academicYear || ''}
          onChange={(e) => handleInputChange('academicYear', e.target.value)}
        />
      </div>

      {/* School Days */}
      <div data-tour="school-days">
        <Label>{t('timetableForm.schoolDays')}</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4 mt-2">
          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(
            (day) => (
              <div key={day} className="flex items-center space-x-2">
                <Switch
                  id={`day-${day}`}
                  checked={timetableData.schoolDays?.includes(day)}
                  onCheckedChange={(checked) => {
                    const updatedDays = checked
                      ? [...(timetableData.schoolDays || []), day]
                      : (timetableData.schoolDays || []).filter((d: string) => d !== day)
                    handleInputChange('schoolDays', updatedDays)
                  }}
                />
                <Label htmlFor={`day-${day}`}>{t('timetableForm.days.' + day)}</Label>
              </div>
            )
          )}
        </div>
      </div>

      {/* Time Settings */}
      <div data-tour="time-settings">
        <Label>{t('timetableForm.timeSettings')}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-2">
          <div>
            <Label htmlFor="startTime">{t('timetableForm.startTime')}</Label>
            <Input
              id="startTime"
              type="time"
              value={timetableData.startTime || '08:00'}
              onChange={(e) => handleInputChange('startTime', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="endTime">{t('timetableForm.endTime')}</Label>
            <Input
              id="endTime"
              type="time"
              value={timetableData.endTime || '17:00'}
              onChange={(e) => handleInputChange('endTime', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="periodCount">{t('timetableForm.numberOfPeriods')}</Label>
            <Input
              id="periodCount"
              type="number"
              min="1"
              value={timetableData.periodCount || 8}
              onChange={(e) =>
                handleInputChange('periodCount', parseInt(e.target.value))
              }
              className="min-h-[44px]"
            />
          </div>
        </div>
      </div>

      {/* Optimization Settings */}
      <div data-tour="optimization-settings">
        <Label>{t('timetableForm.optimizationSettings')}</Label>
        <div className="space-y-4 mt-2">
          <div className="flex items-center space-x-2">
            <Switch
              id="avoidBackToBack"
              checked={timetableData.constraints?.avoidBackToBack}
              onCheckedChange={(checked) =>
                handleInputChange('constraints', {
                  ...timetableData.constraints,
                  avoidBackToBack: checked,
                })
              }
            />
            <Label htmlFor="avoidBackToBack">
              {t('timetableForm.avoidBackToBack')}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="optimizeFreeTime"
              checked={timetableData.constraints?.optimizeFreeTime}
              onCheckedChange={(checked) =>
                handleInputChange('constraints', {
                  ...timetableData.constraints,
                  optimizeFreeTime: checked,
                })
              }
            />
            <Label htmlFor="optimizeFreeTime">
              {t('timetableForm.optimizeFreeTime')}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="balanceTeacherLoads"
              checked={timetableData.constraints?.balanceTeacherLoads}
              onCheckedChange={(checked) =>
                handleInputChange('constraints', {
                  ...timetableData.constraints,
                  balanceTeacherLoads: checked,
                })
              }
            />
            <Label htmlFor="balanceTeacherLoads">
              {t('timetableForm.balanceTeacherLoads')}
            </Label>
          </div>
        </div>
      </div>
    </div>
  )
}
